import type {
  DocNetMission,
  DocNetMissionCreate,
  DocNetMissionDocumentRow,
  DocNetMissionEmailSummary,
  DocNetTimelineResponse,
  DocNetTimelineSource,
} from 'src/@types/docnet';
import DocNetBaseService from 'src/services/docnet/DocNetBaseService';
import { api } from 'src/pages/APIs';

export default class DocNetMissionService extends DocNetBaseService {
  public async getMissions(
    skip?: number,
    limit?: number,
    status?: string,
    search?: string,
    source?: string,
  ): Promise<{ items: DocNetMission[]; total: number }> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();
    if (skip !== undefined) params.set('skip', String(skip));
    if (limit !== undefined) params.set('limit', String(limit));
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    if (source) params.set('source', source);
    const qs = params.toString();
    const url = api.docnet_missions + (qs ? `?${qs}` : '');
    const response = await super.get(url, { headers });
    if (Array.isArray(response)) {
      return { items: response as DocNetMission[], total: response.length };
    }
    const items = (response as any).items || [];
    const total = (response as any).total ?? items.length;
    return { items, total };
  }

  public async getMissionById(
    id: string,
  ): Promise<DocNetMission | undefined> {
    const headers = await this.getHeaders();
    try {
      return (await super.get(`${api.docnet_missions}/${id}`, {
        headers,
      })) as DocNetMission;
    } catch {
      return undefined;
    }
  }

  public async createMission(
    data: DocNetMissionCreate,
  ): Promise<DocNetMission> {
    const headers = await this.getHeaders();
    return (await super.post(api.docnet_missions, data, {
      headers,
    })) as DocNetMission;
  }

  public async updateMission(
    id: string,
    data: Partial<DocNetMission>,
  ): Promise<DocNetMission> {
    const headers = await this.getHeaders();
    return (await super.patch(`${api.docnet_missions}/${id}`, data, {
      headers,
    })) as DocNetMission;
  }

  public async deleteMission(id: string): Promise<void> {
    const headers = await this.getHeaders();
    await super.delete(`${api.docnet_missions}/${id}`, { headers });
  }

  public async restartMission(id: string): Promise<DocNetMission> {
    const headers = await this.getHeaders();
    return (await super.post(`${api.docnet_missions}/${id}/restart`, {}, {
      headers,
    })) as DocNetMission;
  }

  /**
   * Fetch email summaries linked to a mission.  Backed by the
   * server-side aggregate endpoint — no client-side filtering of the
   * global email list.  Response excludes body/attachments.
   */
  public async getMissionEmails(
    missionId: string,
  ): Promise<DocNetMissionEmailSummary[]> {
    const headers = await this.getHeaders();
    const resp = (await super.get(
      `${api.docnet_missions}/${missionId}/emails`,
      { headers },
    )) as { items?: DocNetMissionEmailSummary[] };
    return resp.items ?? [];
  }

  /**
   * Fetch the flat documents list for a mission.  Server-side fan-out
   * to doc2-api produces enriched rows (filename / status / doc_type)
   * — the frontend makes one HTTP call regardless of how many emails
   * or attachments the mission has.
   */
  public async getMissionDocuments(
    missionId: string,
  ): Promise<DocNetMissionDocumentRow[]> {
    const headers = await this.getHeaders();
    const resp = (await super.get(
      `${api.docnet_missions}/${missionId}/documents`,
      { headers },
    )) as { items?: DocNetMissionDocumentRow[] };
    return resp.items ?? [];
  }

  /**
   * Fetch one page of the mission's unified timeline (mission audit +
   * issue audit/activity + agent run events).  The server does the
   * UNION; the client just paginates.
   *
   * Cursor-based pagination on ``(occurred_at, event_id)`` so
   * mid-stream WS appends don't shift items between pages — we never
   * skip or duplicate a row that landed during scrolling.  Pass
   * ``cursor`` from the previous response's ``next_cursor`` to fetch
   * the next older page; ``null`` means there is no older page.
   *
   * Filters are passed verbatim to the server.  Empty arrays are
   * omitted (unfiltered) — passing an empty ``kinds`` parameter would
   * be ambiguous between "everything" and "nothing".
   */
  public async getMissionTimeline(
    missionId: string,
    opts: {
      limit?: number;
      cursor?: string | null;
      kinds?: string[];
      sources?: DocNetTimelineSource[];
    } = {},
  ): Promise<DocNetTimelineResponse> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    if (opts.kinds?.length) params.set('kinds', opts.kinds.join(','));
    if (opts.sources?.length) params.set('sources', opts.sources.join(','));
    const qs = params.toString();
    const url =
      `${api.docnet_missions}/${missionId}/timeline` + (qs ? `?${qs}` : '');
    const resp = (await super.get(url, { headers })) as DocNetTimelineResponse;
    return {
      items: resp.items ?? [],
      next_cursor: resp.next_cursor ?? null,
    };
  }
}
